import type { WalletCreditPayment } from "@b2bportal/hsbc-payment-api";
import type { CreateCardPayment } from "@b2bportal/payment-methods-api";
import { Payment } from "@b2bportal/purchase-api";
import { addFulfillPayment } from "@checkout/states/Cart/helpers";
import {
  getObjectKeysAsObject,
  type ICardPayment,
  ParentState,
  CartState,
} from "@hopper-b2b/checkout";
import { ZERO_FIAT_AMOUNT } from "@hsbc/shared";
import type { FlightMachineContext } from "@hsbc/types";
import { collectBrowserInfo, roundToTwoDecimal } from "@hsbc/utilities";
import { assign } from "xstate";
import type {
  SetRewardsAmountToPayEvent,
  SetRewardsToPayInCashEvent,
  SetRewardsBalanceEvent,
} from "./events";
import type { ParentContextWithRewardsPayment } from "./types";

export const setPaymentFulfillParamsAssigner = (ctx: FlightMachineContext) => {
  const totalPaymentBalance = ctx[CartState.cartQuote].quoteBreakdown?.sellAt ||
    ctx[CartState.cartQuote].quoteBreakdown?.balance || {
      fiat: ZERO_FIAT_AMOUNT,
      accountSpecific: {},
    };

  let awardsPayment;
  let walletCreditPayment: {
    type: Payment;
    value: WalletCreditPayment;
  } | null = null;

  // travel wallet credit payment params
  if (
    ctx.walletPayment?.creditPaymentAmount?.value != null &&
    ctx.walletPayment.creditPaymentAmount.value > 0
  ) {
    const creditAmount = ctx.walletPayment.creditPaymentAmount;
    walletCreditPayment = {
      type: Payment.TravelWalletCredit,
      value: {
        paymentAmount: {
          amount: creditAmount.value,
          currency: creditAmount.currencyCode,
        },
      },
    };
  }

  const { rewardsAmountToApply, rewardsSelected, rewardsAmountToPayInCash } =
    ctx[ParentState.awardsPayment];
  if (rewardsSelected && rewardsAmountToApply > 0) {
    awardsPayment = {
      type: Payment.Rewards,
      value: {
        numberOfPoints: Number(rewardsAmountToApply),
      },
    };
  }

  const cardPaymentPrice = {
    ...totalPaymentBalance.fiat,
    value: roundToTwoDecimal(
      totalPaymentBalance.fiat.value -
        (ctx.walletPayment?.creditPaymentAmount?.value ?? 0) -
        (rewardsAmountToPayInCash?.value ?? 0)
    ),
  };

  const cardPayment: ICardPayment = {
    type: Payment.Card,
    value: {
      paymentAmount: {
        amount: cardPaymentPrice.value,
        currency: cardPaymentPrice.currencyCode,
      },
      paymentId: ctx[ParentState.cardPayment]?.selectedPaymentMethodId ?? "",
    },
  };

  ctx[CartState.cartFulfill].fulfillRequestPayments = [];

  if (cardPaymentPrice.value > 0 && cardPayment.value?.paymentId) {
    addFulfillPayment(cardPayment, ctx);
  }

  if (awardsPayment != null) {
    addFulfillPayment(awardsPayment, ctx);
  }

  if (walletCreditPayment != null) {
    addFulfillPayment(walletCreditPayment, ctx);
  }

  return ctx;
};

export const actions = {
  setRewardsBalance: assign(
    (ctx: ParentContextWithRewardsPayment, event: SetRewardsBalanceEvent) => {
      ctx[ParentState.awardsPayment].rewardsBalance = event.rewardsBalance;
      ctx[ParentState.awardsPayment].rewardsBalanceInCash =
        event.rewardsBalanceInCash;
      return ctx;
    }
  ),
  setRewardsAmountToPay: assign(
    (
      ctx: ParentContextWithRewardsPayment,
      event: SetRewardsAmountToPayEvent
    ) => {
      ctx[ParentState.awardsPayment].rewardsAmountToApply =
        event.rewardsAmountToPay;
      return ctx;
    }
  ),
  setRewardsAmountToPayInCash: assign(
    (
      ctx: ParentContextWithRewardsPayment,
      event: SetRewardsToPayInCashEvent
    ) => {
      ctx[ParentState.awardsPayment].rewardsAmountToPayInCash =
        event.rewardsAmountToPayInCash;
      return ctx;
    }
  ),
  setRewardsSelected: assign((ctx: ParentContextWithRewardsPayment) => {
    ctx[ParentState.awardsPayment].rewardsSelected = true;
    return ctx;
  }),
  setRewardsUnselected: assign((ctx: ParentContextWithRewardsPayment) => {
    ctx[ParentState.awardsPayment].rewardsSelected = false;

    return ctx;
  }),
  // Override from Core
  setPaymentFulfillParams: assign(setPaymentFulfillParamsAssigner),
};

export const createCardPaymentAssigner = (ctx: FlightMachineContext) => {
  const cardPayment = ctx[ParentState.cartFulfill].fulfillRequestPayments?.find(
    (payment) => payment.type === Payment.Card
  );

  if (cardPayment == null) return ctx;

  const createCardPaymentValue: CreateCardPayment = {
    paymentAmount: cardPayment.value.paymentAmount,
    paymentMethodId: {
      value: cardPayment.value.paymentId,
    },
    browserInfo: collectBrowserInfo(),
  };

  cardPayment.value = createCardPaymentValue;

  return ctx;
};

export const ActionTypes = getObjectKeysAsObject(actions);
